import React, { useState, useEffect } from 'react'
import { useHistory } from 'react-router-dom'
import flowRight from 'lodash/flowRight'
import keyBy from 'lodash/keyBy'
import curryRight from 'lodash/curryRight'
import { toast } from '@fielded/shared-ui/src/components/Page'
import { ErrorView, Loading } from '@fielded/shared-ui'
import get from 'lodash/get'

import withConfig from '../../../../../../van-shared/hoc/withConfig'
import { getUppercaseTenant, isPsmWarehouseUser } from '../../../../../../van-shared/utils/utils'
import { withApi } from '../../../../../../common/ApiProvider'
import { withUser } from '../../../../../../common/AuthenticationProvider'
import { VERIFICATION_STATUS, decorateAndFilterShipmentsWithAlerts } from './utils'
import {
  queryStringifyFilters,
  resolveQueryStringFilterState
} from '../../../../../../common/querystring-filters'
import {
  getCurrentPeriod,
  PERIOD_MONTH,
  periodToDateRange
} from '../../../../../../common/periods'

import LMDList from './LMDList'

const PAGE_LIMIT = 50
const validSearchFilters = ['time', 'route', 'state', 'source', 'destination', 'program', 'funder', 'creationDate', 'status', 'thirdParty', 'limit', 'offset', 'verification', 'carrier']

const byId = curryRight(keyBy)

const LMDListContainer = ({ api, user, config }) => {
  const history = useHistory()
  const defaultTime = getCurrentPeriod(PERIOD_MONTH)
  const [error, setError] = useState()
  const [loading, setLoading] = useState(true)
  const [downloadingShipments, setDownloadingShipments] = useState(false)
  const [loadingShipments, setLoadingShipments] = useState(false)
  const [shipments, setShipments] = useState([])
  const [appPrograms, setAppPrograms] = useState()
  const [fundersById, setFundersById] = useState({})
  const [locationsById, setLocationsById] = useState({})
  const [routesById, setRoutesById] = useState({})
  const [carriersById, setCarriersById] = useState({})
  const [totalShipmentsNumber, setTotalShipmentsNumber] = useState(0)
  const [filterOptions, setFilterOptions] = useState({})
  const tenant = getUppercaseTenant(config)

  // Create filters configurations object applying default config as a base
  const availableFilters = validSearchFilters.reduce((acc, filterName) => {
    acc[filterName] = { values: [] }
    return acc
  }, {})

  // Some filters configurations need non-default values
  availableFilters.time.defaultValue = defaultTime
  availableFilters.limit.defaultValue = PAGE_LIMIT
  availableFilters.offset.defaultValue = 0

  // Fetch routes keyed by couchdb_id for only the route IDs present in filter options.
  // Fetching all routes upfront is impractical (thousands exist) and the default
  // listAll limit of 10 would leave most filter entries unresolved.
  const fetchRoutesById = async (filterOptions) => {
    const routeIds = (filterOptions.route || []).filter(Boolean)
    if (!routeIds.length) return {}
    return api.routes.listAll({ filter: { couchdb_id: routeIds, includeDetails: false }, limit: routeIds.length })
      .then(byId('couchdb_id'))
  }

  const initialize = async () => {
    try {
      const country = get(user, 'location.country')
      /* Fetch the data wih parallel requests promises */
      const [
        locationsById,
        fundersById,
        appPrograms,
        { shipments, total },
        filterOptions,
        carriersById
      ] = await Promise.all([
        api.location.listAll().then(byId('_id')).catch(() => { throw new Error('listAll') }),
        api.funders.list().then(byId('_id')).catch(() => { throw new Error('fundersList') }),
        api.program.list().catch(() => { throw new Error('programsList') }),
        getShipments().catch(() => { throw new Error('getShipments') }),
        getFilteringValues().catch(() => { throw new Error('getFilteringValues') }),
        api.carrier.list({country}).then(
          ({organisations}) => {
            return keyBy((organisations || []).map(({organisation}) => organisation), 'id')
          }
        ).catch(() => { throw new Error('carriersList') })
      ])

      const routesById = await fetchRoutesById(filterOptions)
        .catch(() => { throw new Error('routesList') })

      /* Set the state with the results */
      setFilterOptions(filterOptions)
      setShipments(shipments)
      setTotalShipmentsNumber(total)
      setLocationsById(locationsById)
      setFundersById(fundersById)
      setRoutesById(routesById)
      setCarriersById(carriersById)
      setAppPrograms(appPrograms)
    } catch (e) {
      if (e.toString() === 'Error: fundersList') {
        // Do whatever you need to do when the funders list fails
      }
      setError(e)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    initialize()
  }, [])

  const getApiFilters = ({filters = {}, usePaginationOptions = false, withQueryString = true, user = {}}) => {
    let queryStringFilters = {}
    if (withQueryString) {
      queryStringFilters = getQueryStringFilters()
    }
    const effectiveFilter = { ...queryStringFilters, ...filters }
    if (withQueryString) {
      history.push({ ...history.location, search: queryStringifyFilters(effectiveFilter) })
    }

    const isStateLevel = get(user, 'location.level') === 'state'
    const usersState = get(user, 'location.state')
    if (isStateLevel && usersState) {
      effectiveFilter.state = usersState
    }

    // For warehouse users with a warehouse location get shipments from that warehouse
    const userLocationId = get(user, 'location.id', '')
    const attachSource = userLocationId.includes('name') && isPsmWarehouseUser(user)
    if (attachSource) {
      effectiveFilter.source = userLocationId
    }

    const periodDates = periodToDateRange(effectiveFilter.time)

    const paginationOptions = usePaginationOptions && {
      offset: effectiveFilter.offset,
      limit: effectiveFilter.limit
    }

    const translatedFilter = {
      origin_id: effectiveFilter.source,
      program_id: effectiveFilter.program,
      carrier_id: effectiveFilter.carrier,
      route_id: effectiveFilter.route,
      destination_id: effectiveFilter.destination,
      funder_id: effectiveFilter.funder,
      status: effectiveFilter.status,
      created_at: effectiveFilter.creationDate,
      state: effectiveFilter.state,
      created_at_gte: periodDates.from,
      created_at_lte: periodDates.to,
      ...paginationOptions
    }

    // Clean up nulls and undefined values
    const cleanedUpFilter = Object.keys(translatedFilter).reduce((acc, key) => {
      if (translatedFilter[key]) {
        acc[key] = translatedFilter[key]
      }
      return acc
    }, {})

    return cleanedUpFilter
  }

  const exportDeliveries = async () => {
    setDownloadingShipments(true)
    try {
      // Get current filters from query string instead of passing empty object
      const currentFilters = getQueryStringFilters()
      const { shipments: fetchedShipments } = await getShipments(currentFilters, true)

      // Apply verification filter client-side if needed (verification filter is not sent to API)
      const filterAlerts = currentFilters.verification === VERIFICATION_STATUS.UNVERIFIED
      const shipments = decorateAndFilterShipmentsWithAlerts(fetchedShipments, !!filterAlerts)

      const blob = await api.shipment.exportLMDShipments({api, shipments, tenant})
      const link = document.createElement('a')
      const url = URL.createObjectURL(blob)
      link.href = url
      link.download = `shipment-line-export-${new Date().toJSON()}.xlsx`
      document.body.appendChild(link)
      link.click()
      setDownloadingShipments(false)
    } catch (error) {
      setDownloadingShipments(false)
      toast({ title: error.message || `Download Failed`, type: 'warning' })
    }
  }

  const getShipments = async (update = {}, forDownload = false) => {
    const filters = getApiFilters({filters: update, usePaginationOptions: !forDownload, user})
    const params = {filters, ...(forDownload ? { forDownload: true } : {})}
    return api.shipment.listLastMileDeliveries(params)
  }

  const getFilteringValues = async () => {
    // Only time period condition is needed to get the filter values
    const { time } = getQueryStringFilters()
    const filters = getApiFilters({filters: { time }, usePaginationOptions: false, withQueryString: false, user})

    const values = await api.shipment.getLastMileDeliveriesFilterValues(undefined, { filters })

    // ensure null values get converted to undefined for proper later handling
    const valuesWithoutNulls = Object.keys(values).reduce((acc, key) => {
      acc[key] = values[key] || undefined
      return acc
    }, {})

    const {
      origin_id: source = [],
      program_id: program = [],
      carrier_id: carrier = [],
      route_id: route = [],
      destination_id: destination = [],
      funder_id: funder = [],
      status = [],
      created_at: creationDate = [],
      state = []
    } = valuesWithoutNulls
    return {
      source,
      program,
      carrier,
      route,
      destination,
      funder,
      status,
      creationDate,
      state,
      verification: Object.values(VERIFICATION_STATUS)
    }
  }

  const onFilterChange = async (update) => {
    setLoadingShipments(true)
    try {
      const promisesToAwait = [
        getShipments(update)
      ]
      if (update.time) {
        // If the time filter changes, refresh filter options and route names together
        promisesToAwait.push(
          getFilteringValues().then(async (filterOptions) => {
            const routesById = await fetchRoutesById(filterOptions).catch(() => null)
            setFilterOptions(filterOptions)
            if (routesById !== null) setRoutesById(routesById)
          })
        )
      }

      // Any filter option can make an API request apart from the verification filter.
      if (!update.verification) {
        const [{ shipments, total }] = await Promise.all(promisesToAwait)
        setShipments(shipments)
        setTotalShipmentsNumber(total)
      }
    } finally {
      setLoadingShipments(false)
    }
  }

  const getQueryStringFilters = () => {
    const queryStringFilters = resolveQueryStringFilterState(
      history.location.search,
      { sorters: {}, history, availableFilters, withValueValidation: false, withUndefinedValues: false }
    ).filters
    return {
      ...queryStringFilters,
      limit: parseInt(queryStringFilters.limit),
      offset: parseInt(queryStringFilters.offset)
    }
  }

  const onPageChange = (offset) => {
    onFilterChange({ offset })
  }

  if (error) {
    return <ErrorView title='Shipment' error={error} />
  }

  if (loading) {
    return <Loading />
  }

  const updatedFilters = getQueryStringFilters()

  const showViewRoute = !!updatedFilters.route

  let routeLink = ''

  if (showViewRoute) {
    routeLink = `/settings/routePlanning/view/${updatedFilters.route}`
  }

  const filterAlerts = updatedFilters.verification === VERIFICATION_STATUS.UNVERIFIED

  // Decorate shipment with delivery alerts
  const shipmentsWithAlerts = decorateAndFilterShipmentsWithAlerts(shipments, !!filterAlerts)

  return (
    <LMDList
      history={history}
      config={config}
      shipments={shipmentsWithAlerts}
      filters={updatedFilters}
      filterOptions={filterOptions}
      onFilterChange={onFilterChange}
      onPageChange={onPageChange}
      user={user}
      locationsById={locationsById}
      fundersById={fundersById}
      routesById={routesById}
      carriersById={carriersById}
      appPrograms={appPrograms}
      totalShipmentsNumber={totalShipmentsNumber}
      showViewRoute={showViewRoute}
      routeLink={routeLink}
      exportDeliveries={exportDeliveries}
      pageLimit={updatedFilters.limit}
      loadingShipments={loadingShipments}
      downloadingShipments={downloadingShipments}
    />
  )
}

export default flowRight(withApi, withUser, withConfig)(LMDListContainer)
